import React from 'react';
import moment from 'moment';

import Button from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TablePagination from '@mui/material/TablePagination';
import Select from 'components/Select/Select';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';

import { getAdminTransfers, getAdminTransferDetail, approveAdminTransfer } from 'api/admintransfers';
import ImageViewer from 'components/ImageViewer/ImageViewer';

const PAGE_SIZE = 20;
const IMAGE_PROXY_URL = 'https://kot-image-proxy.coinflipsyndicate.online:8443/39bd83260b149297453aa06d71090d57';

const AdminTransfers: React.FC = () => {
    const queryClient = useQueryClient();
    const [selectType, setSelectType] = React.useState('pending');
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(PAGE_SIZE);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [modalOpen, setModalOpen] = React.useState(false);

    const { data, isLoading } = useQuery(['admintransfers', selectType, page, pageSize], getAdminTransfers);

    const detailQuery = useQuery(['admintransfer', selectedId], getAdminTransferDetail, {
        enabled: !!selectedId,
    });

    const confirmMutation = useMutation(() => approveAdminTransfer(selectedId!), {
        onSuccess: () => {
            queryClient.invalidateQueries(['admintransfer', selectedId]);
            queryClient.invalidateQueries(['admintransfers']);
        }
    });

    // Prefetch скриншотов для pending записей
    const prefetchImages = React.useRef<HTMLImageElement[]>([]);
    React.useEffect(() => {
        if (selectType !== 'pending' || !data) return;
        const items = data?.data ?? data ?? [];
        const images: HTMLImageElement[] = [];
        items.forEach((item: any) => {
            [item.receiver_comment, item.receiver_comment2].forEach((field: string) => {
                const match = field?.match(/(https?:\/\/[^\s]+)/);
                if (match) {
                    const img = new Image();
                    img.src = `${IMAGE_PROXY_URL}/image?url=${encodeURIComponent(match[0])}`;
                    images.push(img);
                }
            });
        });
        prefetchImages.current = images;
    }, [data, selectType]);

    const handleSelectChange = (value: string) => {
        setSelectType(value);
        setPage(0);
    };

    const goToDetail = ({ data }: any) => {
        setSelectedId(data.id);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const formatDate = ({ value }: { value: string }) => {
        return value ? moment(value).format('DD MMM YYYY HH:mm') : '';
    };

    const getStatusClass = ({ data }: { data: any }) => {
        return data?.is_approved ? 'bg-green-200' : 'bg-amber-500';
    };

    const formatStatus = ({ data }: { data: any }) => {
        return data?.is_approved ? 'processed' : 'pending';
    };

    const rows = data?.data ?? data ?? [];
    const rowCount = data?.count ?? 0;
    const detail = detailQuery.data;

    return (
        <div className="space-y-4">
            <div className="flex lg:flex-row flex-col lg:space-x-8 lg:space-y-0 space-y-4 mb-8">
                <div className="w-full">
                    <Select
                        label=""
                        options={['pending', 'processed']}
                        value={selectType}
                        onChange={handleSelectChange}
                        multiple={false}
                    />
                </div>
            </div>

            <div className="w-full relative shadow-md rounded overflow-hidden" style={{ height: 800 }}>
                <AgGridReact
                    rowData={rows}
                    overlayNoRowsTemplate={isLoading ? '<span></span>' : '<span>Нет данных</span>'}
                    defaultColDef={{
                        flex: 1,
                        sortable: true,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: { suppressFilterButton: true }
                    }}
                    onRowClicked={goToDetail}
                >
                    <AgGridColumn headerName="Дата создания" field="created_datetime" sort="desc" valueFormatter={formatDate} filter />
                    <AgGridColumn headerName="Отправитель" field="send_username" filter />
                    <AgGridColumn headerName="Получатель" field="receive_username" filter />
                    <AgGridColumn headerName="Рум" field="room_name" filter />
                    <AgGridColumn headerName="Сумма" field="amount" filter />
                    <AgGridColumn headerName="Валюта" field="chip_currency_name" filter />
                    <AgGridColumn headerName="Статус" field="is_approved" valueFormatter={formatStatus} cellClass={getStatusClass} />
                </AgGridReact>
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.8)' }}>
                        <CircularProgress />
                    </div>
                ) : null}
            </div>

            <TablePagination
                component="div"
                count={rowCount}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(0); }}
            />

            <Dialog open={modalOpen} onClose={handleCloseModal} TransitionProps={{ onExited: () => setSelectedId(null) }} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Пополнение №{selectedId}
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative', minHeight: 450 }}>
                    {detailQuery.isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ zIndex: 1 }}>
                            <CircularProgress />
                        </div>
                    )}
                    <div style={{ opacity: detailQuery.isLoading ? 0 : 1 }}>
                        <p className="text-lg font-light mb-4">
                            Дата создания: {detail?.received_datetime ? moment(detail.received_datetime).format('DD MMM YYYY HH:mm') : '—'}
                        </p>
                        <p className="text-lg font-light mb-4">
                            Получатель: {detail?.receive_username ?? '—'}
                        </p>
                        <div className="bg-gray-50 border border-gray-200 rounded py-2 mb-4 inline-block" style={{ marginLeft: '-0.75rem', paddingLeft: '0.75rem', paddingRight: '0.75rem' }}>
                            <p className="text-lg font-light mb-1">
                                Рум: <span className="font-medium">{detail?.room_name ?? '—'}{detail?.receiver_account_balance_snapshot != null ? ` (${detail.receiver_account_balance_snapshot} ${detail?.chip_currency_name ?? ''})` : ''}</span>
                            </p>
                            <p className="text-lg font-light">
                                Сумма: <span className="font-medium">{detail?.amount != null ? `${detail.amount} ${detail?.chip_currency_name ?? ''}` : '—'}</span>
                            </p>
                        </div>
                        {(() => {
                            const transferOverlay = (
                                <>
                                    <span>Рум: <b>{detail?.room_name} ({detail?.receiver_account_balance_snapshot} {detail?.chip_currency_name})</b></span>
                                    <span style={{ margin: '0 12px' }}>·</span>
                                    <span>Сумма: <b>{detail?.amount} {detail?.chip_currency_name}</b></span>
                                </>
                            );
                            return (
                                <table className="text-lg font-light" style={{ borderSpacing: '0 14px', borderCollapse: 'separate', marginBottom: 2 }}>
                                    <tbody>
                                        {/(https?:\/\/[^\s]+)/.test(detail?.receiver_comment || '') && (
                                            <tr>
                                                <td style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>Скрин платежка:</td>
                                                <td><ImageViewer url={detail.receiver_comment.match(/(https?:\/\/[^\s]+)/)[0]} label="Скрин" overlay={transferOverlay} /></td>
                                            </tr>
                                        )}
                                        {/(https?:\/\/[^\s]+)/.test(detail?.receiver_comment2 || '') && (
                                            <tr>
                                                <td style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>Скрин рум:</td>
                                                <td><ImageViewer url={detail.receiver_comment2.match(/(https?:\/\/[^\s]+)/)[0]} label="Скрин" overlay={transferOverlay} /></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            );
                        })()}
                        <p className="text-lg font-light mb-4">
                            Статус: <span className={`px-2 rounded ${detail?.is_approved ? 'bg-green-200' : 'bg-amber-500'}`} style={{ paddingTop: '1.2px', paddingBottom: '1.2px' }}>{detail?.is_approved ? 'processed' : 'pending'}</span>
                        </p>
                        {detail && !detail.is_approved && (
                            <div className="pt-4 text-right">
                                <Button
                                    variant="contained"
                                    color="success"
                                    loading={confirmMutation.isLoading}
                                    onClick={() => confirmMutation.mutate()}
                                >
                                    Подтвердить
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminTransfers;
